import { z, ZodError, type ZodIssue, type ZodType } from 'zod';
import type { FormInputs, FormInputState, StoryBlokBasicFormConfig } from '~/utils/formUtils';
import { collectInputConfig } from '../utils/formUtils'; // Test fails if this function is not imported via relative path - dont know why

const zRequired = z
    .string()
    .nonempty('formElement.fieldIsRequired')
    .or(z.boolean({ error: 'formElement.fieldIsRequired' }))
    .or(z.number({ error: 'formElement.fieldIsRequired' }));
const zEmail = z.email({ error: 'formElement.invalidEmail' });
const zNumberField = z.number({ error: 'formElement.invalidNumber' });
const zRequiredCheckbox = z.literal(true, { error: 'formElement.invalidRequiredCheckbox' });

/**
 * Represents the result of an input validation process.
 *
 * The result is a tuple where:
 * - The first element is a boolean indicating whether the validation was successful.
 * - The second element, which is optional, is an array of strings containing error messages
 *   if the validation failed.
 */
type InputValidationResult = [boolean, string[]?];

/**
 * Represents a type definition for input values accepted by a form. It also includes the possibility of undefined values.
 * */
type FormInput = string | number | boolean | undefined;

/**
 * Retrieves a list of validators based on the specified type and whether the value is required.
 *
 * @param {string} type - The type of the value to validate (e.g., 'email', 'number').
 * @param {boolean} required - Indicates if the value is required.
 * @return {Array<(value: FormInput) => InputValidationResult>} An array of validator functions.
 */
const getValidators = (type: string, required: boolean): Array<(value: FormInput) => InputValidationResult> => {
    const validators = [];
    if (required && type !== 'checkbox') validators.push(Validator.required);
    if (required && type === 'checkbox') validators.push(Validator.requiredCheckbox);
    if (type === 'email') validators.push(Validator.email);
    if (type === 'number') validators.push(Validator.number);
    return validators;
};

/**
 * A utility object for validating form input values based on specified rules and schemas.
 */
export const Validator = {
    /**
     * Validates a form input against a set of validators based on its type and required status.
     *
     * @param {FormInput} value - The value of the form input to be validated.
     * @param {StoryBlokBasicFormConfig} config - The configuration object for the form input.
     * @param {boolean} disabled - Whether the input is disabled
     * @returns {[boolean, string[]]} A tuple where the first element is a boolean indicating if the input is valid,
     * and the second element is an array of potential  error messages.
     */
    validate: (value: FormInput, config: StoryBlokBasicFormConfig, disabled?: boolean): [boolean, string[]] => {
        const required = 'required' in config ? config.required : false;
        const inputType = 'inputType' in config ? config.inputType : undefined;

        // Required selects must not have a disabled option selected
        if (config.component === 'FormSelect' && required && disabled) {
            return [false, ['formElement.invalidSelectOption']];
        }

        // skip any further validations if the value is empty and not required
        if (!required && value === '') return [true, []];

        // if the value is required and empty, we can just return a failure
        if (value === '' && required) return [false, ['formElement.fieldIsRequired']];

        let formIsValid = true;
        const formErrors: string[] = [];

        if (!inputType) return [true, []];
        const validators = getValidators(inputType, required || false);
        validators.forEach((validator) => {
            // ts-expect-error - correct validator signature asserted by getValidators
            const [isValid, validationErrors] = validator(value);
            if (!isValid) {
                formIsValid = false;
                formErrors.push(...validationErrors!);
            }
        });
        return [formIsValid, formErrors];
    },
    /**
     * Validates that a group of inputs meet the minimum required number of filled inputs.
     *
     * @param {FormInputs} formInputs - The current values of the form inputs.
     * @param {FormRequiredGroupStoryblok} groupConfig - The configuration for the required group.
     * @returns {[boolean, string[]]} A tuple where the first element indicates validity, and the second element contains error messages.
     */
    validateRequiredGroup: (formInputs: FormInputs, groupConfig: FormRequiredGroupStoryblok): [boolean, string[]] => {
        let formError: string[] = [];

        const requiredCount = parseInt(groupConfig.minRequired ?? '1', 10);

        const groupFields = collectInputConfig(groupConfig.formElements);
        // Count filled inputs, excluding any disabled select options
        const filledCount = groupFields.filter((field) => {
            const state = formInputs[field.inputKey];
            if (!state) return false;
            // Skip disabled select options
            if (state.disabled) {
                return false;
            }
            return !!state.value;
        }).length;
        const isValid = filledCount >= requiredCount;

        if (!isValid) {
            formError = ['formElement.invalidRequiredGroup'];
        } else {
            formError = [];
        }

        return [isValid, formError];
    },
    /**
     * Parses and validates the given input value against the provided schema.
     *
     * @param {FormInput} value - The input value to be parsed and validated.
     * @param {ZodType} schema - The schema used for validation.
     * @returns {InputValidationResult} A tuple where the first element is a boolean indicating whether the validation succeeded, and the second element (if validation failed) is an array of error messages.
     *
     * If the input value passes validation, the function returns `[true]`.
     * If validation fails due to a schema error, it returns `[false, errorMessages]`, where `errorMessages` contains the validation issues.
     * If an unexpected error occurs, it returns `[false, ['Unbekannter Fehler']]`.
     */
    parse: (value: FormInput, schema: ZodType): InputValidationResult => {
        try {
            schema.parse(value);
            return [true];
        } catch (e: unknown) {
            if (e instanceof ZodError) {
                return [false, Validator.getErrorMessages(e.issues)];
            } else {
                return [false, ['Unbekannter Fehler']];
            }
        }
    },
    /**
     * Validates that the provided form input value exists if required.
     * Ensures the value passes the requirement defined by the `zRequired` schema.
     */
    required: (value: FormInput): InputValidationResult => Validator.parse(value, zRequired),
    /**
     * Validates an email value using a predefined schema.
     */
    email: (value: FormInput): InputValidationResult => Validator.parse(value, zEmail),
    /**
     * Validates a given input value to check if it is a valid number.
     */
    number: (value: FormInput): InputValidationResult => Validator.parse(value, zNumberField),
    /**
     * Validates that a checkbox is true if required
     */
    requiredCheckbox: (value: FormInput): InputValidationResult => Validator.parse(value, zRequiredCheckbox),
    /**
     * Checks if the given value matches the first element in the options array, which is assumed the placeholder / default value
     * If it matches, it considers the field invalid and returns an appropriate error message.
     * Otherwise, the field is considered valid.
     */
    requiredSelect: (value: FormInput, options: string[]): InputValidationResult => {
        if (options[0] === value) {
            return [false, ['formElement.fieldIsRequired']];
        } else {
            return [true];
        }
    },
    getErrorMessages: (errors: ZodIssue[]): string[] => errors.map((error) => error.message)
};

/**
 * Validates the values of a form based on a provided configuration.
 *
 * This function iterates through the given form values and validates
 * each input field using its corresponding configuration. Each input
 * field's validation status and potential errors are updated and returned.
 *
 * @param {FormInputs} formInputs - The current values of the form inputs,
 * where each entry includes an input's value and (potentially) associated errors from the current state.
 * @param {StoryBlokBasicFormConfig[]} inputConfig - The configuration
 * details for the form inputs, including validation requirements.
 * @returns {[boolean, FormInputs]} - A tuple where the first element is
 * a boolean indicating whether all inputs are valid, and the second element
 * is the updated state for all passed inputs, with validation errors included for invalid inputs.
 */
export const validateFormInputs = (
    formInputs: FormInputs,
    inputConfig: StoryBlokBasicFormConfig[]
): [boolean, FormInputs] => {
    const inputConfigs = inputConfig;
    let isValid = true;

    const formInputsWithErrors: FormInputs = Object.entries(formInputs).reduce((acc: FormInputs, [key, entry]) => {
        if (key === 'frc-captcha-response' || key === 'storyUuid') {
            return acc;
        }
        const inputConfig = inputConfigs.find(({ inputKey }) => inputKey === key);
        if (!inputConfig) {
            console.error(`unable to find storyblok properties for input with key: ${key}`);
            return acc;
        }

        // Determine validity, handling required groups and select-disabled cases
        let isInputValid: boolean;
        let errors: string[] = [];
        if (inputConfig.component === 'FormRequiredGroup') {
            [isInputValid, errors] = Validator.validateRequiredGroup(formInputs, inputConfig);
        } else {
            [isInputValid, errors] = Validator.validate(entry.value, inputConfig, entry.disabled);
        }

        // Rebuild state, preserving disabled flag for selects
        const newInputState: FormInputState = {
            value: entry.value,
            ...(entry.disabled ? { disabled: true } : {})
        };

        if (!isInputValid) {
            newInputState.errors = errors;
            isValid = false;
        }

        return {
            ...acc,
            [key]: newInputState
        };
    }, {});
    return [isValid, formInputsWithErrors];
};
